import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  Card,
  ErrorAlert,
  QueryError,
  SkeletonBlock,
  buttonClass,
  useDocumentTitle,
} from "@unizpay/ui";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Breadcrumb } from "../../components/Breadcrumb";
import { PinBoxes } from "../../components/PinBoxes";
import { EmailCodeDialog } from "../../components/VerifyDialogs";
import { api } from "../../lib/api";
import { WALLET_KEY, useWallet } from "../../lib/queries";

type Step = "current" | "new" | "confirm" | "email";

/** What Security shows after the flow: the PIN was set for the first time, or changed. */
export type PinSaved = "set" | "changed";

const COPY: Record<Step, { title: string; text: string }> = {
  current: { title: "Enter your current PIN", text: "To change your PIN, first confirm the one you have." },
  new: { title: "Choose a new PIN", text: "6 digits. Avoid your birthday or repeated digits like 111111." },
  confirm: { title: "Enter it again", text: "To make sure there is no typo." },
  email: { title: "Check your email", text: "Enter the code we emailed you to finish." },
};

/**
 * Setting or changing the PIN, one question at a time. Changing: the current PIN (checked right
 * away, so a wrong one is caught here and not at the end), the new PIN, the new PIN again, then
 * the code we email, in its own modal like every other approval. Setting the first one (at
 * sign-up): the new PIN and the new PIN again. The PIN steps move on by themselves once six
 * digits are typed.
 */
export function PinFlow({
  changing,
  onDone,
  cancel,
}: {
  changing: boolean;
  onDone: (saved: PinSaved) => void | Promise<void>;
  /** A way out (a Cancel link); none at sign-up, where the PIN is required. */
  cancel?: ReactNode;
}) {
  const steps: Step[] = changing ? ["current", "new", "confirm", "email"] : ["new", "confirm"];
  const [step, setStep] = useState<Step>(steps[0]!);
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  // A new code dialog (and so a new emailed code) per attempt; `notice` says why it was replaced.
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  // Closing the code dialog keeps the step: "Enter the code" opens it again, with the same code.
  const [codeOpen, setCodeOpen] = useState(false);

  function restart(message: string) {
    setCurrent("");
    setPin("");
    setConfirm("");
    setError(message);
    setStep(steps[0]!);
  }

  function askForCode(why: string | null = null) {
    setNotice(why);
    setAttempt((a) => a + 1);
    setCodeOpen(true);
    setStep("email");
  }

  const verify = useMutation({
    mutationFn: (value: string) => api.account.verifyWalletPin(value),
    onSuccess: () => {
      setError(null);
      setStep("new");
    },
    onError: (err) => {
      setCurrent("");
      setError(err instanceof ApiError ? err.message : "Could not check your PIN. Try again.");
    },
  });
  const save = useMutation({
    mutationFn: (check?: { check_id: string; code: string }) =>
      api.account.setWalletPin(pin, changing ? current : undefined, check),
    onSuccess: () => onDone(changing ? "changed" : "set"),
    onError: (err) => {
      if (!(err instanceof ApiError)) return;
      // The current PIN stopped working meanwhile (changed elsewhere, or locked): start over.
      if (err.code === "pin_incorrect" || err.code === "pin_locked") restart(err.message);
      // The code ran out before the save: a new one.
      else if (err.code === "code_expired" || err.code === "code_incorrect") askForCode(err.message);
    },
  });

  const index = steps.indexOf(step);

  function back() {
    setError(null);
    save.reset();
    if (step === "email") {
      setConfirm("");
      setStep("confirm");
    } else if (step === "confirm") {
      setConfirm("");
      setPin("");
      setStep("new");
    } else if (step === "new" && changing) {
      setPin("");
      setCurrent("");
      setStep("current");
    }
  }

  function onComplete(value: string) {
    if (step === "current") verify.mutate(value);
    else if (step === "new") {
      setError(null);
      setStep("confirm");
    } else if (value !== pin) {
      setPin("");
      setConfirm("");
      setError("The PINs did not match. Choose your new PIN again.");
      setStep("new");
    } else {
      setError(null);
      // A change is approved with the emailed code; a first PIN saves straight away.
      if (changing) askForCode();
      else save.mutate(undefined);
    }
  }

  const value = step === "current" ? current : step === "new" ? pin : confirm;
  const setValue = step === "current" ? setCurrent : step === "new" ? setPin : setConfirm;
  const busy = verify.isPending || save.isPending;
  const saveError =
    save.error instanceof ApiError && /^(code|pin)_/.test(save.error.code) ? null : save.error;

  return (
    <div className="mx-auto max-w-md py-4 text-center">
      <p className="text-xs font-medium text-fg-muted">
        Step {index + 1} of {steps.length}
      </p>
      <div className="mt-2 flex justify-center gap-1.5" aria-hidden="true">
        {steps.map((s, i) => (
          <span key={s} className={i <= index ? "h-1 w-8 rounded bg-accent" : "h-1 w-8 rounded bg-line"} />
        ))}
      </div>
      <h2 className="mt-5 text-lg font-semibold">{COPY[step].title}</h2>
      <p className="mt-1 text-[0.8125rem] text-fg-muted">{COPY[step].text}</p>

      {step === "email" ? (
        <div className="mt-6">
          {save.isPending ? (
            <p className="text-[0.8125rem] text-fg-muted">Changing your PIN…</p>
          ) : (
            <Button variant="secondary" onClick={() => setCodeOpen(true)}>
              Enter the code
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-6 flex justify-center">
          <PinBoxes
            // A fresh field per step, so it takes the focus and starts empty.
            key={step}
            label={COPY[step].title}
            value={value}
            onChange={(v) => {
              setValue(v);
              if (v.length < 6) setError(null);
            }}
            onComplete={onComplete}
            error={error}
            disabled={busy}
          />
        </div>
      )}
      {step === "current" && (
        <p className="mt-4 text-xs text-fg-muted">After 5 wrong tries, the PIN locks for a while.</p>
      )}
      {verify.isPending && <p className="mt-4 text-[0.8125rem] text-fg-muted">Checking…</p>}
      {saveError && (
        <div className="mt-4 text-left">
          <ErrorAlert error={saveError} title="Could not save your PIN" />
        </div>
      )}
      <div className="mt-8 flex items-center justify-between gap-2">
        {index > 0 ? (
          <Button variant="ghost" onClick={back} disabled={busy}>
            <ArrowLeft aria-hidden="true" /> Back
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {cancel}
          {!changing && step === "confirm" && saveError && (
            <Button onClick={() => save.mutate(undefined)} loading={save.isPending}>
              Try again
            </Button>
          )}
        </div>
      </div>
      {changing && attempt > 0 && (
        <EmailCodeDialog
          key={attempt}
          open={step === "email" && !save.isPending && !save.isSuccess && codeOpen}
          notice={notice}
          request={api.account.pinCode}
          verify={(start, code) => api.account.verifyPinCode(start.check_id, code)}
          onVerified={(start, code) => {
            setCodeOpen(false);
            save.mutate({ check_id: start.check_id, code });
          }}
          onCancel={() => setCodeOpen(false)}
        />
      )}
    </div>
  );
}

/** Account → Security → Set or change PIN. */
export function PinPage() {
  const wallet = useWallet();
  const changing = wallet.data?.pin_set ?? false;
  useDocumentTitle(changing ? "Change PIN" : "Set PIN");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if (wallet.isPending) return <SkeletonBlock label="Loading your PIN status" lines={3} />;
  if (wallet.isError) {
    return (
      <QueryError
        error={wallet.error}
        title="Could not load your PIN status"
        onRetry={() => wallet.refetch()}
        retrying={wallet.isFetching}
      />
    );
  }

  return (
    <>
      <Breadcrumb
        parent={{ to: "/account/security", label: "Security" }}
        current={changing ? "Change PIN" : "Set PIN"}
      />
      <Card>
        <PinFlow
          // Remounts if the PIN gets set elsewhere meanwhile.
          key={String(changing)}
          changing={changing}
          onDone={async (saved) => {
            await queryClient.invalidateQueries({ queryKey: WALLET_KEY });
            navigate("/account/security", { state: { pinSaved: saved } });
          }}
          cancel={
            <Link to="/account/security" className={buttonClass("secondary")}>
              Cancel
            </Link>
          }
        />
      </Card>
    </>
  );
}
